import { type Facing, screenHeading, toScreen, toWorld, type Entity, type GameMap, type IsoOrigin } from '@nine/shared';

const FACINGS: readonly Facing[] = ['n', 'e', 's', 'w'];
import { globeBend, globeUnbend } from './globe';
import { mapOrigin } from './terrain';
import { pitchFactors } from './pitchView';

/** How the world picture is zoomed onto the screen: its scale and where its corner lands (CSS pixels). */
export interface Lens {
  readonly z: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A point in the window (client pixels) → world pixels, for a world picture `px` CSS pixels per
 * art pixel, drawn with world pixel (x, y) at (x + ox, y + oy), seen through `lens`.
 */
export function clientToWorld(
  lens: Lens,
  px: number,
  ox: number,
  oy: number,
  clientX: number,
  clientY: number,
): [number, number] {
  // the picture's texel under the cursor, through the Globe View's bend (globe.ts; flat when it is off)
  const [tx, ty] = globeUnbend((clientX - lens.x) / lens.z / px, (clientY - lens.y) / lens.z / px);
  return [tx - ox, ty - oy];
}

/** Inverse of {@link clientToWorld}. */
export function worldToClient(
  lens: Lens,
  px: number,
  ox: number,
  oy: number,
  sx: number,
  sy: number,
): [number, number] {
  const [tx, ty] = globeBend(sx + ox, sy + oy);
  return [tx * px * lens.z + lens.x, ty * px * lens.z + lens.y];
}

/** A 2×2 linear map on world pixels, as a canvas transform's a, b, c, d (x' = a x + c y). */
export type Spin = readonly [a: number, b: number, c: number, d: number];

/**
 * The isometric camera: the angle the world is seen from.
 * The simulation always works in map coordinates; everything drawn or clicked goes through here,
 * so turning the camera only rotates the picture, never the rules.
 *
 * The world's art (the ground, buildings, trees) is painted for the four quarter turns (`rot`).
 * Between them (`spin`, a turn past `rot`), the ground plane is turned as a whole about the map's
 * middle and everything standing is moved upright to where it now stands: positions (`proj`,
 * `unproj`, `depth`) and headings follow the whole angle; `turn`, `unturn` and `view` keep to the
 * quarter turn the art is painted for, and `flat` (the ground layer, drawn turned as one picture
 * by render/passes/turn.ts) makes `proj` and `depth` keep to it too.
 */
export class IsoCamera {
  /** Quarter turns clockwise, 0–3. */
  rot = 0;
  /** The turn past `rot` (radians, clockwise like it), between −π/4 and π/4 for a nearest `rot`. */
  private spinA = 0;
  private cs = 1;
  private sn = 0;
  /**
   * C0-ROTATE pitch: the camera's elevation (degrees, 30 = the painted 2:1 view, lower = flatter).
   * The ground's depth on screen goes with sin(pitch) (`groundK`, 1 at 30°) about the map's
   * middle; heights with cos(pitch) (`heightK`, 1 at 30°).
   */
  pitch = 30;
  groundK = 1;
  heightK = 1;
  /**
   * While true, `proj` and `depth` give the view at `rot` alone: set while the ground layer is drawn,
   * which is turned on screen as a whole picture instead.
   */
  flat = false;
  origin: IsoOrigin;
  /** The map's views at each angle, kept per map (a zone's and each building floor's). */
  private readonly views = new WeakMap<GameMap, GameMap[]>();
  private maps: GameMap[] = [];

  constructor(private map: GameMap) {
    if (map.w !== map.h) throw new Error('Camera rotation needs a square map');
    this.origin = mapOrigin(map);
    this.views.set(map, this.maps);
  }

  /** The map the camera looks at. */
  get current(): GameMap {
    return this.map;
  }

  /**
   * Looks at another map (going into a building, up its stairs and out again: interiors.ts swaps
   * the World's map). Every projection follows from the next call on.
   */
  setMap(map: GameMap): void {
    if (map === this.map) return;
    if (map.w !== map.h) throw new Error('Camera rotation needs a square map');
    this.map = map;
    this.origin = mapOrigin(map);
    let maps = this.views.get(map);
    if (!maps) this.views.set(map, (maps = []));
    this.maps = maps;
  }

  /** The turn past `rot`, in radians. */
  get spin(): number {
    return this.spinA;
  }

  /** The whole angle, in quarter turns (0 to 4). */
  get angle(): number {
    return this.rot + this.spinA / (Math.PI / 2);
  }

  /** An angle (quarter turns, any value) as `angle` gives it back: 0 to 4, its nearest quarter as `rot`. */
  wrap(quarters: number): number {
    // C0-ROTATE: as `angle` gives it back with `rot` pinned at 0 (−2 to 2, see setAngle).
    const w = ((quarters % 4) + 4) % 4;
    return w > 2 ? w - 4 : w;
  }

  /** Eighth turns from the first angle, for headings (two per quarter turn). */
  get octants(): number {
    return this.rot * 2 + this.spinA / (Math.PI / 4);
  }

  /**
   * Looks from any angle (quarter turns, unwrapped): the nearest quarter turn becomes `rot`, the
   * rest the turn past it.
   */
  setAngle(quarters: number): void {
    // C0-ROTATE: free yaw. The art stays at its first quarter turn (`rot` 0) and the whole angle is
    // the turn past it, round the full circle: the ground plane turns as one picture, buildings and
    // stone blocks are drawn from their painted views at any angle (render/turned/), figures pick
    // their eight headings from the whole angle. No quarter switch, so no cross-fade and no repaint.
    this.rot = 0;
    const w = ((quarters % 4) + 4) % 4;
    this.spinA = (w > 2 ? w - 4 : w) * (Math.PI / 2);
    if (Math.abs(this.spinA) < 1e-9) this.spinA = 0;
    this.cs = Math.cos(this.spinA);
    this.sn = Math.sin(this.spinA);
  }

  /**
   * How world pixels move when the ground turns by `a` radians (about any point on it): the ground
   * is squashed 2:1 on screen, so a turn of it is the rotation stretched by that squash.
   */
  spinMatrix(a: number = this.spinA): Spin {
    const c = Math.cos(a);
    const s = Math.sin(a);
    // C0-ROTATE: then the pitch's squash of the ground's depth (screen y about the map's middle).
    const k = this.groundK;
    return [c, (k * s) / 2, -2 * s, k * c];
  }

  /**
   * How world pixels already on screen (turned and pitched) move when the ground turns on by `a`
   * radians: the turn seen through the pitch's squash (for keeping the camera on the player).
   */
  turnPx(a: number): Spin {
    const c = Math.cos(a);
    const s = Math.sin(a);
    const k = this.groundK;
    return [c, (k * s) / 2, (-2 * s) / k, c];
  }

  /** Sets the pitch (degrees, clamped to 0–90; lane CAMERA-PITCH, pitchView.ts). True if it changed. */
  setPitch(deg: number): boolean {
    const p = Math.max(0, Math.min(90, deg));
    if (p === this.pitch) return false;
    this.pitch = p;
    const k = pitchFactors(p);
    this.groundK = k.groundK;
    this.heightK = k.heightK;
    this.figureK = k.figureK;
    this.viewTan = k.viewTan;
    return true;
  }

  /** How much taller a figure (a billboard) is drawn than painted: `heightK` below 30°, 1 from 30° up. */
  figureK = 1;
  /** The projection's view direction, rise over run (tan 30° at the painted view), for which faces show. */
  viewTan = Math.tan(Math.PI / 6);

  /** Whether the view differs from the painted one at all (turned or pitched). */
  get warped(): boolean {
    return this.spinA !== 0 || this.groundK !== 1;
  }

  /** Screen y of the view at the whole angle → pitched (about the map's middle). */
  private squash(y: number): number {
    if (this.groundK === 1) return y;
    const cy = this.centre()[1];
    return cy + (y - cy) * this.groundK;
  }

  private unsquash(y: number): number {
    if (this.groundK === 1) return y;
    const cy = this.centre()[1];
    return cy + (y - cy) / this.groundK;
  }

  /** The middle of the map in world pixels: the point every turn is about (the same at any angle). */
  centre(): [number, number] {
    const h = this.map.w / 2;
    return toScreen(h, h, this.origin);
  }

  /** A world pixel of the view at `rot` (the painted art) → where it is at the whole angle. */
  lift(sx: number, sy: number): [number, number] {
    if (!this.warped) return [sx, sy];
    const [cx, cy] = this.centre();
    const [a, b, c, d] = this.spinMatrix();
    const [x, y] = [sx - cx, sy - cy];
    return [cx + a * x + c * y, cy + b * x + d * y];
  }

  /** Inverse of {@link lift}. */
  drop(sx: number, sy: number): [number, number] {
    if (!this.warped) return [sx, sy];
    const [cx, cy] = this.centre();
    const [a, b, c, d] = this.spinMatrix();
    const det = a * d - b * c;
    const [x, y] = [sx - cx, sy - cy];
    return [cx + (d * x - c * y) / det, cy + (a * y - b * x) / det];
  }

  /** How much nearer the viewer a ground point of the view at `rot` (world pixels) is at the whole angle. */
  liftDepth(sx: number, sy: number): number {
    if (!this.spinA) return 0;
    const [a, b] = toWorld(sx, sy, this.origin);
    const [c, d] = this.spun(a, b);
    return c + d - (a + b);
  }

  /**
   * The canvas transform that turns a picture of the view at `rot` into the whole angle, for a
   * picture drawn with world pixel (x, y) at (x + ox, y + oy).
   */
  groundTransform(ox: number, oy: number): [number, number, number, number, number, number] {
    const [a, b, c, d] = this.spinMatrix();
    const [cx, cy] = this.centre();
    const [px, py] = [cx + ox, cy + oy];
    return [a, b, c, d, px - (a * px + c * py), py - (b * px + d * py)];
  }

  /**
   * The rectangle (world pixels) of the view at `rot` that covers a rectangle at the whole angle,
   * for finding the painted scenery that lands on screen.
   */
  viewRect(x0: number, x1: number, y0: number, y1: number): [number, number, number, number] {
    if (!this.warped) return [x0, x1, y0, y1];
    const xs: number[] = [];
    const ys: number[] = [];
    for (const [x, y] of [
      [x0, y0],
      [x1, y0],
      [x0, y1],
      [x1, y1],
    ] as const) {
      const [u, v] = this.drop(x, y);
      xs.push(u);
      ys.push(v);
    }
    return [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  }

  /** Coordinates of the view at `rot` → at the whole angle (turned about the map's middle). */
  private spun(x: number, y: number): [number, number] {
    const h = this.map.w / 2;
    const [dx, dy] = [x - h, y - h];
    return [h + this.cs * dx - this.sn * dy, h + this.sn * dx + this.cs * dy];
  }

  /** Inverse of {@link spun}. */
  private unspun(x: number, y: number): [number, number] {
    const h = this.map.w / 2;
    const [dx, dy] = [x - h, y - h];
    return [h + this.cs * dx + this.sn * dy, h - this.sn * dx + this.cs * dy];
  }

  /** Map coordinates → coordinates of the map as seen at the current quarter turn. */
  turn(x: number, y: number): [number, number] {
    const n = this.map.w;
    switch (this.rot) {
      case 1:
        return [n - y, x];
      case 2:
        return [n - x, n - y];
      case 3:
        return [y, n - x];
      default:
        return [x, y];
    }
  }

  /** Inverse of {@link turn}. */
  unturn(x: number, y: number): [number, number] {
    const n = this.map.w;
    switch (this.rot) {
      case 1:
        return [y, n - x];
      case 2:
        return [n - x, n - y];
      case 3:
        return [n - y, x];
      default:
        return [x, y];
    }
  }

  /** Map coordinates → world pixels on screen (of the painted view at `rot` alone while `flat`). */
  proj(x: number, y: number): [number, number] {
    const v = this.turn(x, y);
    if (this.flat) return toScreen(...v, this.origin);
    const [sx, sy] = toScreen(...(this.spinA ? this.spun(...v) : v), this.origin);
    return [sx, this.squash(sy)];
  }

  /** Map coordinates → world pixels of the painted view at `rot` (the ground layer's). */
  projView(x: number, y: number): [number, number] {
    return toScreen(...this.turn(x, y), this.origin);
  }

  /** World pixels on screen → map coordinates (always at the whole angle: what the mouse is over). */
  unproj(sx: number, sy: number): [number, number] {
    const v = toWorld(sx, this.unsquash(sy), this.origin);
    return this.unturn(...(this.spinA ? this.unspun(...v) : v));
  }

  /** Draw-order key: larger is nearer the viewer. */
  depth(x: number, y: number): number {
    const v = this.turn(x, y);
    const [a, b] = this.spinA && !this.flat ? this.spun(...v) : v;
    return a + b;
  }

  /** A direction on the map → the same direction in the turned view (inverse of {@link dirToMap}). */
  dirTurn(dx: number, dy: number): [number, number] {
    const [x, y] = this.quarterDir(dx, dy);
    return this.spinA ? [this.cs * x - this.sn * y, this.sn * x + this.cs * y] : [x, y];
  }

  /** A direction on the map → the same direction in the view at `rot`. */
  private quarterDir(dx: number, dy: number): [number, number] {
    switch (this.rot) {
      case 1:
        return [-dy, dx];
      case 2:
        return [-dx, -dy];
      case 3:
        return [dy, -dx];
      default:
        return [dx, dy];
    }
  }

  /** A direction in the turned view (e.g. from the arrow keys) → the same direction on the map. */
  dirToMap(dx: number, dy: number): [number, number] {
    if (this.spinA) [dx, dy] = [this.cs * dx + this.sn * dy, -this.sn * dx + this.cs * dy];
    switch (this.rot) {
      case 1:
        return [dy, -dx];
      case 2:
        return [-dx, -dy];
      case 3:
        return [-dy, dx];
      default:
        return [dx, dy];
    }
  }

  /**
   * C0-ROTATE: the on-screen direction an entity walked last at the current angle (view-space map
   * vector), when its heading still comes from that walk (not from facing a target since): exact at
   * any yaw, where the eight-step heading plus the camera's octants would round twice.
   */
  private walkDir(e: { heading: number; dirX?: number; dirY?: number }): [number, number] | null {
    const { dirX, dirY } = e;
    if (dirX === undefined || dirY === undefined || (!dirX && !dirY)) return null;
    if (screenHeading(dirX, dirY) !== (((e.heading % 8) + 8) % 8)) return null;
    return this.dirTurn(dirX, dirY);
  }

  /**
   * Screen heading of an entity at the current angle (0 = screen-right, clockwise, eight steps): its
   * world heading minus the camera's yaw. The one place figures pick their drawn view from (the
   * renderer, lookAt, every NPC and monster); a switch to more drawn views changes only this and
   * `facing`.
   */
  heading(e: { heading: number; dirX?: number; dirY?: number }): number {
    const v = this.walkDir(e);
    if (v) return screenHeading(v[0], v[1]);
    return ((Math.round(e.heading + this.octants) % 8) + 8) % 8;
  }

  /** Screen left/right facing at the current angle. */
  facing(e: Entity): 1 | -1 {
    const v = this.walkDir(e);
    if (v) {
      const sx = v[0] - v[1];
      if (Math.abs(sx) > 1e-3) return sx > 0 ? 1 : -1;
    }
    const h = this.heading(e);
    if (h === 0 || h === 1 || h === 7) return 1;
    if (h === 3 || h === 4 || h === 5) return -1;
    // Straight up or down the screen: keep the remembered side, flipped when the view is reversed.
    return Math.abs(this.spinA) > Math.PI / 2 ? (-e.facing as 1 | -1) : e.facing;
  }

  /** The map rearranged as seen at the current angle, for drawing terrain (cached per angle). */
  view(): GameMap {
    const cached = this.maps[this.rot];
    if (cached) return cached;
    const m = this.map;
    const n = m.w;
    const tiles = new Uint8Array(n * n);
    const variant = new Uint8Array(n * n);
    // An authored layout's surfaces (roads, sand) turn with the tiles.
    const surface = m.surface ? new Uint8Array(n * n) : undefined;
    for (let j = 0; j < n; j++)
      for (let i = 0; i < n; i++) {
        // Tile (i, j) covers [i, i+1) × [j, j+1); its turned centre picks the turned tile.
        const [ti, tj] = this.turn(i + 0.5, j + 0.5).map(Math.floor) as [number, number];
        tiles[tj * n + ti] = m.tiles[j * n + i]!;
        variant[tj * n + ti] = m.variant[j * n + i]!;
        if (surface) surface[tj * n + ti] = m.surface![j * n + i]!;
      }
    const pt = (p: { x: number; y: number }) => {
      const [x, y] = this.turn(p.x, p.y);
      return { x, y };
    };
    // Buildings: the turned footprint, and a gate's beam swings to the other axis on odd turns.
    // A terrace's stairs turn with it; its spans are counted from the other end of the wall when
    // the turn has reversed the wall's direction (+x or +y along it).
    const structures = m.structures.map((st) => {
      const [ax, ay] = this.turn(st.i, st.j);
      const [bx, by] = this.turn(st.i + st.w, st.j + st.h);
      const axis = st.axis && this.rot % 2 ? (st.axis === 'x' ? 'y' : 'x') : st.axis;
      let turned: { facing: Facing; stairs: readonly (readonly [number, number])[] } | null = null;
      if (st.facing) {
        const facing = FACINGS[(FACINGS.indexOf(st.facing) + this.rot) % 4]!;
        const [wx, wy] = st.facing === 'n' || st.facing === 's' ? [1, 0] : [0, 1];
        const [tx, ty] = this.quarterDir(wx, wy);
        const flip = tx + ty < 0;
        const len = facing === 'n' || facing === 's' ? Math.abs(bx - ax) : Math.abs(by - ay);
        const stairs = (st.stairs ?? []).map(([a, b]) =>
          flip ? ([len - b, len - a] as const) : ([a, b] as const),
        );
        turned = { facing, stairs };
      }
      return {
        ...st,
        i: Math.min(ax, bx),
        j: Math.min(ay, by),
        w: Math.abs(bx - ax),
        h: Math.abs(by - ay),
        ...(axis ? { axis } : {}),
        ...(turned ?? {}),
      };
    });
    const v: GameMap = {
      ...m,
      tiles,
      variant,
      ...(surface ? { surface } : {}),
      structures,
      plaza: pt(m.plaza),
      lair: pt(m.lair),
      spawn: pt(m.spawn),
      elder: pt(m.elder),
    };
    this.maps[this.rot] = v;
    return v;
  }
}
